// ─── Ticketing Service ─────────────────────────────────────────────────────
import firebase, { db, auth } from "../firebase/config";
import { createTicket } from "../firebase/firestoreService";

// Platform service fee added on top of the ticket's face value at checkout —
// this is store profit, separate from what the event owner earns. Owner
// revenue (EventOwnerCard) is computed from the base ticketTypes price, so
// this fee never touches that calculation; it only affects what the buyer
// is charged.
export const TICKET_SERVICE_FEE_RATE = 0.03;

export const calcTicketTotal = (subtotal) => {
  const fee = Math.round(subtotal * TICKET_SERVICE_FEE_RATE);
  return { subtotal, fee, total: subtotal + fee };
};

// Reserves capacity before payment is initiated, so nobody pays for a
// sold-out slot. Pure concurrency correctness (no trust boundary) — safe to
// run as a client-side Firestore transaction.
export const reserveTickets = async (eventId, selections) => {
  const ref = db.collection("events").doc(eventId);
  await db.runTransaction(async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists) throw new Error("Event not found.");
    const event = snap.data();
    const ticketTypes = (event.ticketTypes || []).map((tt) => {
      const sel = selections.find((s) => s.ticketTypeId === tt.id);
      if (!sel) return tt;
      const remaining = (tt.capacity || 0) - (tt.sold || 0);
      if (sel.qty > remaining) {
        throw new Error(`Only ${remaining} left for "${tt.name}".`);
      }
      return { ...tt, sold: (tt.sold || 0) + sel.qty };
    });
    tx.update(ref, { ticketTypes });
  });
};

// Mirror of reserveTickets — called when a payment is declined or times out,
// so a failed purchase doesn't permanently shrink an event's capacity.
export const releaseTickets = async (eventId, selections) => {
  const ref = db.collection("events").doc(eventId);
  await db.runTransaction(async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists) return;
    const event = snap.data();
    const ticketTypes = (event.ticketTypes || []).map((tt) => {
      const sel = selections.find((s) => s.ticketTypeId === tt.id);
      if (!sel) return tt;
      return { ...tt, sold: Math.max(0, (tt.sold || 0) - sel.qty) };
    });
    tx.update(ref, { ticketTypes });
  });
};

// Issues tickets directly once the client observes payment succeed — same
// trust model as createOrder for physical products.
export const finalizeTicketPurchase = async ({ chargeId, eventId, eventName, eventDate, buyer, selections }) => {
  try {
    const tickets = [];
    for (const sel of selections) {
      for (let i = 0; i < sel.qty; i++) {
        const code = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
        const id = await createTicket({
          eventId, eventName: eventName || "", eventDate: eventDate ? new Date(eventDate) : null,
          ticketTypeId: sel.ticketTypeId, ticketTypeName: sel.name || "", price: sel.price || 0,
          buyerName: buyer?.name || "", buyerEmail: buyer?.email || "", buyerPhone: buyer?.phone || "",
          userId: auth.currentUser?.uid || null,
          chargeId,
          code,
          status: "Paid",
        });
        tickets.push({ id, code });
      }
    }
    return { success: true, tickets };
  } catch (err) {
    console.error("finalizeTicketPurchase error:", err);
    return { success: false, error: "Could not issue tickets. Please contact support with reference " + chargeId };
  }
};

// Scanning — client-side Firestore transaction so two near-simultaneous
// scans of the same ticket can't both succeed. Access to the Scan page
// itself is already gated by isAdmin/isEventStaff (see ScanPage.js).
export const validateTicket = async ({ ticketId, code }) => {
  try {
    const ref = db.collection("tickets").doc(ticketId);
    const result = await db.runTransaction(async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists) throw new Error("Ticket not found.");

      const ticket = snap.data();
      if (ticket.code !== code) throw new Error("Invalid ticket code.");

      if (ticket.status === "Used") {
        const when = ticket.usedAt?.toDate?.().toLocaleString() || "an earlier time";
        throw new Error(`Already used at ${when}.`);
      }
      if (ticket.status !== "Paid") throw new Error("Ticket is not valid for entry.");

      tx.update(ref, {
        status: "Used",
        usedAt: firebase.firestore.FieldValue.serverTimestamp(),
        usedBy: auth.currentUser?.uid || "",
        usedByName: auth.currentUser?.displayName || auth.currentUser?.email || "Staff",
      });

      return { eventName: ticket.eventName, ticketTypeName: ticket.ticketTypeName, buyerName: ticket.buyerName };
    });
    return { success: true, ...result };
  } catch (err) {
    return { success: false, error: err.message || "Validation failed." };
  }
};
